/* eslint-disable complexity */
'use client'

import { useAuthState } from '@/app/providers/auth'
import Link, { useLanguage } from '@/components/Link'
import { getDeliveryTimeLabel } from '@/components/page-specific/catering/common/helpers/useDeliveryTimeLabel'
import type { AppErrors } from '@/components/page-specific/catering/common/types/AppErrors'
import { useAddressLabel } from '@/components/page-specific/checkout/hooks/useAddressLabel'
import { showZendesk } from '@/components/Providers/SupportProvider/zendesk'
import countries from '@/generated/countries.json'
import type { UserBasketSettingsLocationFragment } from '@/generated/graphql'
import { getDeliveryDetailsForMixpanel, useMixPanel } from '@/helpers/mixpanelConfig'
import { getCountryCodeFromName } from '@/helpers/multiRegion/countries'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'
import { getCountryFromLocale } from '@fusion/locale/parsing'
import {
  AccessTime,
  CalendarToday,
  EditCalendar,
  LocationOn,
  Person,
  Close,
  AccessAlarm,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import { format } from 'date-fns'
import { enGB, nl, de, it } from 'date-fns/locale'
import { capitalize } from 'lodash'
import { useParams, usePathname, useSearchParams } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { UserBasketSettings } from '../../../common/domain/user-basket-settings'
import { useAppStateStore } from '../../../common/store/appStateStore'
import AddressLookup from '../../../supplier-menu/components/input-bar/address-lookup'
import { SupplierInfoTile } from './supplier-info-tile'

const CustomIconForDatepicker = () => {
  return <EditCalendar fontSize="small" />
}

type Props = {
  userBasketSettings: UserBasketSettings
  minAllowedDate: Date
  timeSlots: string[]
  onUserBasketSettingsChange: (settings: UserBasketSettings) => void
  called: boolean
  vendorType?: string
  action?: React.ReactNode
}

const SupplierAvailabilitySelector: React.FC<Props> = ({
  userBasketSettings,
  timeSlots,
  minAllowedDate,
  onUserBasketSettingsChange,
  called,
  vendorType,
  action,
}) => {
  const { t } = useTranslation()
  const tenant = useTenant()
  const authState = useAuthState()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const params = useParams()
  const currentPathWithQuery = useMemo(() => {
    const queryString = searchParams?.toString() ?? ''
    return queryString ? `${pathname}?${queryString}` : pathname
  }, [pathname, searchParams])
  const errors = useAppStateStore((state) => state.errors)
  const setErrors = useAppStateStore((state) => state.setErrors)
  const updateUserState = useAppStateStore((state) => state.updateUserState)
  const accountName = authState.type === 'authenticated' ? authState.user.account?.name || '' : ''
  const availableAccountLocations = useMemo(
    () => (authState.type === 'authenticated' ? (authState.user.account?.locations ?? []) : []),
    [authState],
  )

  const [modalOpen, toggleModal] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [timeDialogOpen, setTimeDialogOpen] = useState(false)
  const [headcountDialogOpen, setHeadcountDialogOpen] = useState(false)
  const [headcountDraft, setHeadcountDraft] = useState<string>(
    userBasketSettings.headCount ? String(userBasketSettings.headCount) : '',
  )
  const timeAnchorRef = useRef<HTMLDivElement | null>(null)
  const dateAnchorRef = useRef<HTMLDivElement | null>(null)

  const shortLeadPopupEnabled = useFeatureFlag(SPLITS.SHORT_LEAD_TIME_POP_UP, {
    tenantId: tenant.id,
  })
  const [shortLeadPopupOpen, setShortLeadPopupOpen] = useState(false)
  const lastShownSelectionRef = useRef<string | null>(null)
  const { addressLabel: pinAddress } = useAddressLabel(userBasketSettings.location)
  const language = useLanguage()
  const datePickerLang =
    language === 'nl' ? nl : language === 'de' ? de : language === 'it' ? it : enGB

  const vendorTypeTranslated =
    vendorType === 'catering'
      ? t('marketplace:catering', 'catering')
      : vendorType === 'pantry'
        ? t('marketplace:pantry', 'pantry')
        : vendorType === 'all'
          ? t('marketplace:all', 'all')
          : vendorType || ''

  const localeFromParams = useMemo(() => {
    const langParam = params?.lang
    if (typeof langParam === 'string') return langParam
    if (Array.isArray(langParam) && langParam.length > 0) return langParam[0]
    return tenant.defaultLocale
  }, [params, tenant.defaultLocale])

  const localeCountryCode = useMemo(() => {
    if (!localeFromParams) return undefined
    return getCountryFromLocale(localeFromParams)?.toUpperCase()
  }, [localeFromParams])

  const localeCountryName = useMemo(() => {
    if (!localeCountryCode) return undefined
    const countryEntry = (countries as { code: string; name: string }[]).find(
      (country) => country.code.toUpperCase() === localeCountryCode,
    )
    return countryEntry?.name ?? localeCountryCode
  }, [localeCountryCode])

  const addressCountryCode = useMemo(() => {
    if (!userBasketSettings.location) return undefined
    const location = basketLocationToAddress(userBasketSettings.location)
    return getCountryCodeFromName(location?.country)?.toUpperCase()
  }, [userBasketSettings.location])

  const hasLocaleMismatch =
    Boolean(userBasketSettings.location) &&
    Boolean(localeCountryCode && addressCountryCode && localeCountryCode !== addressCountryCode)

  const shouldForceAddressDialog = !userBasketSettings.location || hasLocaleMismatch

  const defaultAddressError = t('marketplace:addressRequired', 'Please enter a valid address.')

  const addressErrorMessage = useMemo(() => {
    if (!shouldForceAddressDialog) return undefined
    if (!userBasketSettings.location) {
      return defaultAddressError
    }
    if (hasLocaleMismatch) {
      return t('marketplace:addressCountryMismatch', {
        defaultValue: `Please select an address in ${localeCountryName || localeCountryCode}.`,
        country: localeCountryName || localeCountryCode,
      })
    }
    return undefined
  }, [
    defaultAddressError,
    hasLocaleMismatch,
    localeCountryCode,
    localeCountryName,
    shouldForceAddressDialog,
    t,
    userBasketSettings.location,
  ])

  const mergeErrors = useCallback(
    (updated: Partial<AppErrors>) => {
      const next = { ...errors, ...updated }
      const sanitized = Object.fromEntries(
        Object.entries(next).filter(([, value]) => value !== undefined && value !== null),
      ) as AppErrors
      setErrors(sanitized)
    },
    [errors, setErrors],
  )

  // Open address modal on mount if no location is set
  const hasShownInitialModal = useRef(false)
  useEffect(() => {
    if (hasShownInitialModal.current) return
    if (shouldForceAddressDialog) {
      hasShownInitialModal.current = true
      const desiredError = addressErrorMessage ?? defaultAddressError
      mergeErrors({ address: desiredError })
      toggleModal(true)
    }
  }, [shouldForceAddressDialog, addressErrorMessage, defaultAddressError, mergeErrors])

  useEffect(() => {
    if (!shouldForceAddressDialog && errors.address) {
      mergeErrors({ address: undefined })
    }
  }, [shouldForceAddressDialog, errors.address, mergeErrors])

  useEffect(() => {
    if (!shouldForceAddressDialog) toggleModal(false)
  }, [shouldForceAddressDialog])

  useEffect(() => {
    if (headcountDialogOpen) {
      setHeadcountDraft(
        userBasketSettings.headCount && userBasketSettings.headCount > 0
          ? String(userBasketSettings.headCount)
          : '',
      )
    }
  }, [headcountDialogOpen, userBasketSettings.headCount])

  const resetHeadcountDraft = () => {
    setHeadcountDraft(
      userBasketSettings.headCount && userBasketSettings.headCount > 0
        ? String(userBasketSettings.headCount)
        : '',
    )
  }

  const closeModal = () => {
    const needsLocation = !userBasketSettings.location
    const needsLocaleMatch = hasLocaleMismatch
    const shouldKeepOpen = needsLocation || needsLocaleMatch || Boolean(errors.address)
    toggleModal(shouldKeepOpen)
    if (needsLocation || needsLocaleMatch) {
      mergeErrors({
        address: (needsLocaleMatch && addressErrorMessage) || defaultAddressError,
      })
    }
  }

  const handleAddressChange = async (location: UserBasketSettingsLocationFragment) => {
    updateUserState({ location })
  }

  const locationLabel = t('marketplace:address', 'Address')
  const dateLabel = t('marketplace:date', 'Date')
  const timeLabel = t('marketplace:time', 'Time')
  const headcountLabel = t('marketplace:headcount', 'Headcount')

  const locationDisplay = pinAddress ?? t('marketplace:deliverTo')
  const isLocationPlaceholder = !pinAddress

  const dateInfo = useMemo(() => {
    if (!userBasketSettings.date) {
      return { display: dateLabel, isPlaceholder: true }
    }

    try {
      const parsedDate = new Date(userBasketSettings.date)
      if (Number.isNaN(parsedDate.getTime())) {
        throw new Error('Invalid date')
      }
      return {
        display: format(parsedDate, 'EEE d MMM', { locale: datePickerLang }),
        isPlaceholder: false,
      }
    } catch {
      return { display: dateLabel, isPlaceholder: true }
    }
  }, [userBasketSettings.date, dateLabel, datePickerLang])

  const timeInfo = useMemo(() => {
    if (!userBasketSettings.time) {
      return { display: timeLabel, isPlaceholder: true }
    }

    const label = getDeliveryTimeLabel(tenant, userBasketSettings.time)
    return {
      display: label || timeLabel,
      isPlaceholder: !label,
    }
  }, [tenant, timeLabel, userBasketSettings.time])

  const headcountInfo = useMemo(() => {
    if (userBasketSettings.headCount && userBasketSettings.headCount > 0) {
      const count = userBasketSettings.headCount
      return {
        display: t('marketplace:peopleCount', {
          count,
          defaultValue: `${count} ${count === 1 ? 'person' : 'people'}`,
        }),
        isPlaceholder: false,
      }
    }

    return { display: headcountLabel, isPlaceholder: true }
  }, [headcountLabel, t, userBasketSettings.headCount])

  const canSelectDate = Boolean(userBasketSettings.location)
  const canSelectTime = Boolean(userBasketSettings.date)
  const canSelectHeadcount = Boolean(userBasketSettings.date && userBasketSettings.time)

  const handleDateSelection = (newDate: Date | null) => {
    if (!newDate) return

    const timestamp = newDate.getTime()
    if (Number.isNaN(timestamp)) return

    updateUserState({ date: timestamp })
    mergeErrors({ date: undefined })
    setDatePickerOpen(false)
  }

  const handleTimeSelect = (timeslot: string) => {
    onUserBasketSettingsChange({ ...userBasketSettings, time: timeslot })
    mergeErrors({ time: undefined })
    setTimeDialogOpen(false)
  }

  const handleHeadcountConfirm = () => {
    const numericValue = Number(headcountDraft)
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      mergeErrors({
        headCount: t('marketplace:headcountError', 'Enter a valid headcount'),
      })
      return
    }

    mergeErrors({ headCount: undefined })
    onUserBasketSettingsChange({ ...userBasketSettings, headCount: numericValue })
    setHeadcountDialogOpen(false)
  }

  const handleHeadcountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeadcountDraft(event.target.value)
    if (errors.headCount) {
      mergeErrors({ headCount: undefined })
    }
  }

  const handleHeadcountDialogClose = () => {
    setHeadcountDialogOpen(false)
    resetHeadcountDraft()
    if (errors.headCount) {
      mergeErrors({ headCount: undefined })
    }
  }

  const getSelectionKey = (date?: number | null, time?: string | null) => {
    if (!date || !time) return null
    return `${date}-${time}`
  }

  const mixPanel = useMixPanel()
  const auth = useAuthState()
  useEffect(() => {
    if (!shortLeadPopupEnabled) return
    const dateTs = userBasketSettings.date
    const time = userBasketSettings.time
    if (!dateTs || !time) return

    const selectionKey = getSelectionKey(dateTs, time)
    if (!selectionKey) return
    if (lastShownSelectionRef.current === selectionKey) return

    const target = new Date(dateTs)

    // Extract start time from "HH:MM - HH:MM"
    const rangeMatch = /^\s*(\d{1,2}):(\d{2})\s*-\s*\d{1,2}:\d{2}/.exec(time)

    if (!rangeMatch) {
      console.error('Invalid time range format for short lead popup:', {
        time,
        expectedFormat: 'HH:MM - HH:MM',
      })
      return
    }

    const hh = Number(rangeMatch[1])
    const mm = Number(rangeMatch[2])

    target.setHours(hh, mm, 0, 0)

    const diffHours = (target.getTime() - Date.now()) / (1000 * 60 * 60)

    if (diffHours > 0 && diffHours < 18) {
      setShortLeadPopupOpen(true)
      lastShownSelectionRef.current = selectionKey
      try {
        const deliveryDetails = getDeliveryDetailsForMixpanel(userBasketSettings)
        mixPanel?.track('Search Assist', {
          name: 'Search Assist',
          ...deliveryDetails,
          customerAccount:
            auth.type === 'authenticated'
              ? `${auth.user.account?.id}-${auth.user.account?.name}`
              : 'guest',
          tenantName: tenant.name,
          tenantId: tenant.id,
          userId: auth.type === 'authenticated' ? auth.user?.id : null,
          type: 'Short Leadtime',
        })
      } catch (e) {
        console.error('Mixpanel tracking failed', e)
      }
    } else {
      setShortLeadPopupOpen(false)
    }
  }, [userBasketSettings.date, userBasketSettings.time, shortLeadPopupEnabled])

  const allowSignIn = authState.type !== 'authenticated' && tenant.permissions.allowUserSignIn

  const tenantSupportPhone = tenant?.contact?.regions?.find(
    (r) => r?.countryCode.toLowerCase() === localeCountryCode?.toLowerCase(),
  )?.phone?.support

  const phoneRaw = String(tenantSupportPhone ?? '')
  const phoneTel = phoneRaw.replace(/\s+/g, '')

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLang}>
      <Stack spacing={1} minWidth={{ xs: '100%' }}>
        <Paper
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: 2,
            p: { md: 1.5, sm: 0 },
            overflow: 'hidden',
            boxShadow: 'none',
            border: (theme) => theme.borders.default,
          }}>
          {' '}
          <Box
            paddingX={2}
            paddingY={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between">
            {accountName ? (
              <Typography
                color="text.primary"
                variant="subtitle3"
                sx={{ fontSize: { xs: 14, sm: 14, md: 16 } }}>
                {t('marketplace:orderingForVendorType', {
                  vendorType: capitalize(vendorTypeTranslated),
                })}{' '}
                <Typography
                  component="span"
                  color="primary.main"
                  sx={{ fontWeight: 600, fontSize: 'inherit' }}>
                  {accountName}
                </Typography>
              </Typography>
            ) : (
              <Typography
                width={{ xs: '100%', lg: 'unset' }}
                color="text.primary"
                variant="subtitle3"
                fontSize={{ xs: 11, sm: 14, md: 16 }}>
                {t('marketplace:orderingForVendorType', { vendorType })}
              </Typography>
            )}
            {action}
          </Box>
          <Grid
            container
            sx={(theme) => ({
              '--Grid-borderWidth': '1px',
              borderTop: 'var(--Grid-borderWidth) solid',
              borderColor: theme.borderColors.default,
              '& > div:not(:last-of-type)': {
                borderRight: 'var(--Grid-borderWidth) solid',
                borderColor: theme.borderColors.default,
              },
            })}>
            <Grid size={{ xs: 3, md: 6 }}>
              <SupplierInfoTile
                icon={<LocationOn fontSize="small" />}
                label={locationLabel}
                value={locationDisplay}
                isPlaceholder={isLocationPlaceholder}
                error={Boolean(errors.address)}
                helperText={errors.address}
                onClick={() => {
                  mergeErrors({ address: undefined })
                  toggleModal(true)
                }}
              />
            </Grid>{' '}
            <Grid size={{ xs: 3, md: 2 }} ref={dateAnchorRef}>
              <SupplierInfoTile
                icon={<CalendarToday fontSize="small" />}
                label={dateLabel}
                value={dateInfo.display}
                isPlaceholder={dateInfo.isPlaceholder}
                disabled={!canSelectDate}
                onClick={() => setDatePickerOpen(true)}
              />
            </Grid>
            <Grid size={{ xs: 3, md: 2 }} ref={timeAnchorRef}>
              <SupplierInfoTile
                icon={<AccessTime fontSize="small" />}
                label={timeLabel}
                value={timeInfo.display}
                isPlaceholder={timeInfo.isPlaceholder}
                disabled={!canSelectTime}
                onClick={() => setTimeDialogOpen(true)}
              />
            </Grid>
            <Grid size={{ xs: 3, md: 2 }}>
              <SupplierInfoTile
                icon={<Person fontSize="small" />}
                label={headcountLabel}
                value={headcountInfo.display}
                isPlaceholder={headcountInfo.isPlaceholder}
                disabled={!canSelectHeadcount}
                onClick={() => setHeadcountDialogOpen(true)}
                error={Boolean(errors.headCount)}
              />
            </Grid>
          </Grid>
        </Paper>
      </Stack>
      <DatePicker
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        onAccept={(date) => handleDateSelection(date ? new Date(date.valueOf()) : null)}
        onChange={(date) => handleDateSelection(date ? new Date(date.valueOf()) : null)}
        value={userBasketSettings.date ? new Date(userBasketSettings.date) : null}
        minDate={minAllowedDate}
        disabled={!canSelectDate}
        slots={{
          openPickerIcon: CustomIconForDatepicker,
        }}
        slotProps={{
          textField: {
            sx: { display: 'none' },
          },
          actionBar: {
            actions: ['clear', 'cancel', 'accept'] as const,
          },
          popper: {
            anchorEl: () => dateAnchorRef?.current as HTMLElement,
            placement: 'bottom-start',
          },
          desktopPaper: {
            sx: {
              borderRadius: 4,
            },
          },
        }}
      />
      <Dialog
        open={timeDialogOpen}
        onClose={() => setTimeDialogOpen(false)}
        disableScrollLock
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              py: 2,
            },
          },
        }}>
        <DialogContent>
          <Typography variant="h5">
            {t('marketplace:selectDeliveryTime', 'Select delivery time')}
          </Typography>
          {timeSlots.length ? (
            <List>
              {timeSlots.map((timeslot) => (
                <ListItemButton
                  key={timeslot}
                  onClick={() => handleTimeSelect(timeslot)}
                  selected={userBasketSettings.time === timeslot}>
                  <ListItemText primary={getDeliveryTimeLabel(tenant, timeslot)} />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">
              {t('marketplace:noTimesAvailable', 'No delivery times available')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeDialogOpen(false)}>
            {t('marketplace:cancel', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <Popper
        open={shortLeadPopupOpen}
        anchorEl={timeAnchorRef.current}
        placement="bottom"
        sx={(theme) => ({
          zIndex: theme.zIndex.modal,
        })}>
        <Paper
          sx={(theme) => ({
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
            p: 2,
            borderRadius: 4,
            maxWidth: 400,
            position: 'relative',
            overflow: 'visible',
          })}>
          {/* arrow */}
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: -6,
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 12,
              height: 12,
              bgcolor: theme.palette.primary.main,
            })}
          />

          {/* icon */}
          <AccessAlarm
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: '50%',
              p: 1,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.common.white,
              position: 'absolute',
              top: -6,
              left: -6,
            })}
          />

          {/* close */}
          <IconButton
            size="small"
            onClick={() => setShortLeadPopupOpen(false)}
            sx={{ color: 'common.white', position: 'absolute', top: 0, right: 4 }}>
            <Close fontSize="small" />
          </IconButton>

          {/* content */}
          <Stack spacing={1} sx={{ mr: 4, ml: 4 }}>
            <Typography variant="subtitle3" color="common.white">
              {t('marketplace:shortLeadTimeTitle')}
            </Typography>

            <Typography variant="caption" color="common.white">
              <Trans
                i18nKey="marketplace:shortLeadTimeHelp"
                values={{ tenantPhone: phoneRaw }}
                components={{
                  tel: (
                    <Box
                      component="a"
                      href={`tel:${phoneTel}`}
                      sx={{ color: 'common.white', textDecoration: 'underline' }}
                    />
                  ),
                  chat: (
                    <Box
                      component="a"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        showZendesk()
                      }}
                      sx={{ color: 'common.white', textDecoration: 'underline' }}
                    />
                  ),
                }}
              />
            </Typography>
          </Stack>
        </Paper>
      </Popper>
      <Dialog
        open={headcountDialogOpen}
        onClose={handleHeadcountDialogClose}
        disableScrollLock
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              py: 2,
            },
          },
        }}>
        <DialogContent>
          <Typography variant="h5">{headcountLabel}</Typography>
          <TextField
            fullWidth
            type="number"
            margin="dense"
            value={headcountDraft}
            onChange={handleHeadcountChange}
            error={Boolean(errors.headCount)}
            helperText={
              errors.headCount
                ? t('marketplace:headcountError', 'Enter a valid headcount')
                : undefined
            }
            slotProps={{
              input: {
                inputProps: { min: 1 },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleHeadcountDialogClose}>
            {t('marketplace:cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={handleHeadcountConfirm}>
            {t('marketplace:confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        disableScrollLock
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              py: 2,
            },
          },
        }}>
        <DialogContent
          sx={{
            py: 2,
          }}>
          <Stack direction="column" spacing={2}>
            <Typography variant="h5">{t('marketplace:deliveryAddress')}</Typography>
            <AddressLookup
              error={errors.address}
              selectedLocation={userBasketSettings.location}
              onAddressChanged={handleAddressChange}
              availableAccountLocations={availableAccountLocations}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: allowSignIn ? 'space-between' : 'flex-end',
              }}>
              {allowSignIn && (
                <Typography>
                  {t('marketplace:recentAddress')}{' '}
                  <Link href={`/authentication/login?destination=${currentPathWithQuery}`} passHref>
                    <Button>{t('marketplace:signIn')}</Button>
                  </Link>
                </Typography>
              )}
              <Button variant="contained" onClick={closeModal}>
                {t('marketplace:confirm')}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  )
}

export { SupplierAvailabilitySelector }
